import { useSearchParams } from "react-router-dom";
import {
  Page,
  Table,
  type Column,
  Input,
  Select,
  Text,
  DateTimeField,
  EmptyState,
} from "@marekl/adminer/components";
import { useDebouncedSearchFilter, useOptionalFilters } from "@marekl/adminer/hooks";
import { useAuditLogList, type AuditLog } from "../../lib/queries/auditLogs";
import { usePagedList } from "../../lib/usePagedList";
import { JsonBlock } from "../../components/JsonBlock";
import { ListCard } from "../../components/ListCard";
import {
  ACTION_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  labelAction,
  labelEntityType,
} from "./auditLabels";

export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action") ?? "";
  const entityType = searchParams.get("entity_type") ?? "";
  const q = searchParams.get("q") ?? "";

  const { data, isLoading, error, refetch, pagination, filterReset, summary } =
    usePagedList(
      useAuditLogList,
      {
        action: action || undefined,
        entity_type: entityType || undefined,
        q: q || undefined,
      },
      {
        searchParams,
        setSearchParams,
        nounOf: ["záznamu", "záznamů"],
      },
    );

  function applyFilter(key: string, value: string) {
    filterReset((p) => {
      if (value) {
        p.set(key, value);
      } else {
        p.delete(key);
      }
    });
  }

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => applyFilter("q", next),
  });

  const items = data?.items ?? [];

  const columns: Column<AuditLog>[] = [
    {
      key: "created_at",
      header: "Čas",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    {
      key: "user",
      header: "Uživatel",
      render: (row) => row.username || <Text variant="muted">systém</Text>,
    },
    {
      key: "action",
      header: "Akce",
      primary: true,
      render: (row) => labelAction(row.action),
    },
    {
      key: "entity",
      header: "Objekt",
      render: (row) =>
        row.entity_type ? (
          <span>
            {labelEntityType(row.entity_type)}
            {row.entity_id != null && (
              <>
                {" "}
                <Text variant="mono">{row.entity_id}</Text>
              </>
            )}
          </span>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "ip",
      header: "IP",
      render: (row) => <Text variant="mono">{row.ip}</Text>,
    },
    {
      key: "details",
      header: "Detail",
      render: (row) =>
        row.details_json ? (
          <JsonBlock json={row.details_json} label="Zobrazit" />
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
  ];

  const filters = useOptionalFilters({
    filters: [
      {
        key: "q",
        label: "Hledat",
        alwaysOn: true,
        wide: true,
        active: !!q,
        onClear: () => applyFilter("q", ""),
        render: () => (
          <Input label="Hledat" type="search" value={qDraft} placeholder="uživatel" onChange={(e) => setQDraft(e.target.value)} />
        ),
      },
      {
        key: "action",
        label: "Akce",
        active: !!action,
        onClear: () => applyFilter("action", ""),
        render: () => (
          <Select label="Akce" value={action} onChange={(e) => applyFilter("action", e.target.value)}>
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        ),
      },
      {
        key: "entity_type",
        label: "Objekt",
        active: !!entityType,
        onClear: () => applyFilter("entity_type", ""),
        render: () => (
          <Select label="Objekt" value={entityType} onChange={(e) => applyFilter("entity_type", e.target.value)}>
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        ),
      },
    ],
  });

  // Records are append-only: no "+ nová", only browsing and filtering.
  return (
    <Page>
      <Page.Content>
        <ListCard title="Seznam záznamů" filters={filters} pagination={pagination} summary={summary}>
          <Table<AuditLog>
            striped
            cards
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="Žádné záznamy"
                description="Vybraným filtrům neodpovídá žádný záznam."
              />
            }
          />
        </ListCard>
      </Page.Content>
    </Page>
  );
}
